/**
 * Customer data layer — platform API only (post-Supabase).
 *
 * Repayments go through the in-app mobile-money prompt (POST /payments/repay):
 * the customer taps Repay, approves with their PIN on the phone, and the
 * schedule updates when the provider confirms.
 */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useToast } from "@/hooks/use-toast";
import { platformApi } from "@/platform/api";
import { usePlatformAuth } from "@/platform/PlatformAuthContext";
import {
  getLendingRules,
  getPlanRules,
  normalizePlanTier,
  type LendingRules,
  type SettingValue,
} from "@/lib/lendingRules";

export interface CustomerProfile {
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  role: string;
  plan_tier?: string | null;
  status: string;
  created_at: string;
  monthly_income?: number;
  employment_status?: string;
  credit_score?: number;
  national_id?: string;
}

export interface CustomerLoan {
  id: string;
  application_id: string;
  principal_amount: number;
  interest_rate: number;
  term_months: number;
  monthly_payment: number;
  outstanding_balance: number;
  total_amount?: number;
  status: string;
  disbursement_date: string;
  due_date: string;
  user_id: string;
  loan_type?: string;
  created_at: string;
}

export interface CustomerApplication {
  id: string;
  amount: number;
  purpose: string;
  term_months: number;
  status: string;
  application_date: string;
  user_id: string;
  decision_reason?: string | null;
}

export interface CustomerInstallment {
  id: string;
  loan_id: string;
  sequence: number;
  due_date: string;
  amount_due: number;
  amount_paid: number;
  status: string;
}

export interface CustomerTransaction {
  id: string;
  amount: number;
  transaction_type: string;
  status: string;
  created_at: string;
  reference?: string;
  loan_id?: string;
}

interface CustomerContextValue {
  profile: CustomerProfile | null;
  kycStatus: string | null;
  loans: CustomerLoan[];
  loanApplications: CustomerApplication[];
  installments: CustomerInstallment[];
  transactions: CustomerTransaction[];
  settings: SettingValue[];
  isLoading: boolean;

  // Derived
  activeLoan: CustomerLoan | null;
  planTier: string;
  premiumUnlocked: boolean;
  kycApproved: boolean;
  kycComplete: boolean;
  planRules: ReturnType<typeof getPlanRules>;
  lendingRules: ReturnType<typeof getLendingRules>;
  totalOutstanding: number;
  pendingApplications: number;

  // Actions
  fetchData: () => Promise<void>;
  handleSignOut: () => Promise<void>;
  handleLencoPayment: (loan: CustomerLoan, amount: number, label?: string) => Promise<void>;
  handleLencoSettlement: (loan: CustomerLoan) => Promise<void>;

  // Loading states
  lencoPaymentLoading: string | null;
  lencoSettlementLoading: string | null;

  // Legacy MFA surface — customers authenticate with SMS codes now; these
  // remain as inert stubs until AccountPage is rebuilt in Phase 3.
  mfaFactor: null;
  startMfaEnrollment: () => Promise<null>;
  verifyMfaEnrollment: (code: string, factorId: string, challengeId: string) => Promise<void>;
  disableMfa: () => Promise<void>;
  mfaLoading: boolean;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx;
}

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { user, signOut, refreshUser } = usePlatformAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loans, setLoans] = useState<CustomerLoan[]>([]);
  const [loanApplications, setLoanApplications] = useState<CustomerApplication[]>([]);
  const [installments, setInstallments] = useState<CustomerInstallment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [serverRules, setServerRules] = useState<LendingRules | null>(null);
  const [lencoPaymentLoading, setLencoPaymentLoading] = useState<string | null>(null);
  const [lencoSettlementLoading, setLencoSettlementLoading] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const [loansRes, appsRes] = await Promise.all([
        platformApi.myLoans(),
        platformApi.myApplications(),
        refreshUser(),
        // Non-blocking: if this fails the bundled defaults still apply.
        platformApi.myLendingRules()
          .then((r) => setServerRules({
            minAmount: r.min_amount,
            maxAmount: r.max_amount,
            maxTermMonths: r.max_term_months,
            interestRate: r.interest_rate,
            approvalSlaHours: 48,
          }))
          .catch(() => undefined),
      ]);

      setLoans(loansRes.loans.map((l) => ({
        id: l.id,
        application_id: "",
        principal_amount: l.principal_amount,
        interest_rate: l.interest_rate,
        term_months: l.term_months,
        monthly_payment: l.monthly_payment,
        outstanding_balance: l.outstanding_balance,
        total_amount: l.total_amount,
        status: l.status,
        disbursement_date: l.disbursement_date || "",
        due_date: l.due_date || "",
        user_id: user.id,
        created_at: "",
      })));
      setInstallments(loansRes.loans.flatMap((l) =>
        l.installments.map((i) => ({
          id: `${l.id}:${i.sequence}`,
          loan_id: l.id,
          sequence: i.sequence,
          due_date: i.due_date || "",
          amount_due: i.amount_due,
          amount_paid: i.amount_paid,
          status: i.status,
        }))
      ));
      setLoanApplications(appsRes.applications.map((a) => ({
        id: a.id,
        amount: a.amount,
        purpose: a.purpose || "",
        term_months: a.term_months,
        status: a.status,
        application_date: a.application_date || "",
        user_id: user.id,
        decision_reason: a.decision_reason,
      })));
    } catch (err) {
      console.error("Error fetching customer data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) fetchData();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  /** One-tap repayment: pushes a PIN prompt to the customer's phone. */
  const handleLencoPayment = async (loan: CustomerLoan, amount: number, label?: string) => {
    try {
      setLencoPaymentLoading(loan.id);
      const intent = await platformApi.repay(loan.id, amount);
      toast({
        title: "Check your phone",
        description: intent.message || "Enter your mobile money PIN to approve the payment.",
      });
      // Poll briefly so an approved payment reflects without a manual refresh.
      const poll = async (attempt: number) => {
        if (attempt > 20) return;
        await new Promise((r) => setTimeout(r, 6000));
        try {
          const status = await platformApi.intentStatus(intent.id);
          if (status.status === "succeeded") {
            toast({ title: "Payment received", description: "Your balance has been updated." });
            await fetchData();
            return;
          }
          if (["failed", "expired"].includes(status.status)) {
            toast({
              title: "Payment not completed",
              description: status.status_reason || "The payment was not approved.",
              variant: "destructive",
            });
            return;
          }
        } catch { /* keep polling */ }
        await poll(attempt + 1);
      };
      void poll(1);
    } catch (error) {
      toast({
        title: "Payment error",
        description: error instanceof Error ? error.message : "Could not start the payment.",
        variant: "destructive",
      });
    } finally {
      setLencoPaymentLoading(null);
    }
  };

  const handleLencoSettlement = async (loan: CustomerLoan) => {
    setLencoSettlementLoading(loan.id);
    try {
      await handleLencoPayment(loan, Number(loan.outstanding_balance), "Settlement");
    } finally {
      setLencoSettlementLoading(null);
    }
  };

  // Profile mapped from the platform user.
  const profile: CustomerProfile | null = user ? {
    user_id: user.id,
    full_name: user.full_name || "",
    email: user.email || "",
    phone: user.phone || "",
    role: user.role,
    plan_tier: user.plan_tier,
    status: "active",
    created_at: "",
  } : null;

  const kycStatus = user ? (user.kyc_status === "none" ? null : user.kyc_status) : null;

  // Derived values (server quote is binding; these power instant previews).
  const settings: SettingValue[] = [];
  const lendingRules = getLendingRules(settings);
  const planTier = normalizePlanTier(user?.plan_tier);
  const kycApproved = kycStatus === "approved";
  const kycComplete = kycStatus !== null;
  const premiumUnlocked = planTier === "premium" && kycApproved;
  // Prefer the server's configured product over the bundled defaults — an admin
  // can change the limits, and a slider offering more than the server will
  // approve is a rejection the borrower did not deserve.
  const fallbackRules = getPlanRules(premiumUnlocked ? "premium" : "standard", lendingRules);
  const planRules = serverRules ?? fallbackRules;
  const activeLoan = loans.find((l) => l.status === "active" || l.status === "late") || null;
  const totalOutstanding = loans.reduce((sum, l) => sum + Number(l.outstanding_balance || 0), 0);
  const pendingApplications = loanApplications.filter((a) => a.status === "pending").length;

  const mfaNotice = async () => {
    toast({
      title: "Not needed",
      description: "Your account is secured with SMS sign-in codes.",
    });
    return null;
  };

  return (
    <CustomerContext.Provider
      value={{
        profile, kycStatus, loans, loanApplications, installments,
        transactions: [], settings, isLoading,
        activeLoan, planTier, premiumUnlocked, kycApproved, kycComplete, planRules, lendingRules,
        totalOutstanding, pendingApplications,
        fetchData, handleSignOut, handleLencoPayment, handleLencoSettlement,
        lencoPaymentLoading, lencoSettlementLoading,
        mfaFactor: null,
        startMfaEnrollment: mfaNotice,
        verifyMfaEnrollment: async () => { await mfaNotice(); },
        disableMfa: async () => { await mfaNotice(); },
        mfaLoading: false,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}
